import { useQuery } from '@tanstack/react-query';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { queryKeys } from './query-client';
import { fetchWorkspaces } from './workspaces';
import { fetchWorkspaceFiles } from './workspace-files';
import { resolveVfsContent, workspaceFileRefOf } from './vfs-attachment';

export interface AttachmentContent {
  /** What the doc viewer / open+download helpers consume: the attachment's URL, or the resolved workspace
   *  file body for a `vfsPath` artifact. Empty when there is nothing (yet) to show. */
  content: string;
  /** True while a `vfsPath` is still resolving (workspaces or workspace-files loading) — the caller keeps
   *  the chip in a pending state rather than flashing an "unavailable". */
  pending: boolean;
}

// Resolution reads the SHARED React Query caches directly (`queryKeys.workspaces` / `.workspaceFiles`)
// rather than the `useWorkspaces()` / `useWorkspaceFiles()` hooks, on purpose: those hooks each open a
// duplexer WEBSOCKET per call (`useFileSystemDuplexer` etc.), and a chat can render many attachment chips
// — one socket per chip is a leak. A chip only needs to READ the already-loaded list (the sidebar's
// `useWorkspaces` keeps it fresh); it doesn't need its own live subscription. Same key → React Query
// dedupes, so a plain-URL attachment (workspace-files disabled) adds no request and no socket.

/** The source for one attachment. A URL attachment IS its url. An agent-authored artifact carried by
 *  `vfsPath` (url empty) is resolved against the workspace's own files — see `vfs-attachment.ts` for why
 *  and how, and the WEB-ONLY caveat. */
export function useAttachmentContent(attachment: MessageAttachment): AttachmentContent {
  const url = attachment.url?.trim() ?? '';
  const ref = url ? null : attachment.vfsPath ? workspaceFileRefOf(attachment.vfsPath) : null;

  const workspaces = useQuery({ queryKey: queryKeys.workspaces, queryFn: fetchWorkspaces, enabled: !!ref });
  const workspaceId = ref ? workspaces.data?.find((w) => w.slug === ref.slug)?.id : undefined;
  const files = useQuery({
    queryKey: queryKeys.workspaceFiles(workspaceId),
    queryFn: () => fetchWorkspaceFiles(workspaceId as string),
    enabled: !!workspaceId,
  });

  if (url) return { content: url, pending: false };
  if (!ref) return { content: '', pending: false };
  if (workspaces.isLoading) return { content: '', pending: true };
  if (!workspaceId) return { content: '', pending: false };
  if (files.isLoading) return { content: '', pending: true };
  if (!files.data) return { content: '', pending: false };
  return { content: resolveVfsContent(attachment.vfsPath as string, files.data) ?? '', pending: false };
}
